import { useMemo } from 'react';
import { useAllComments } from '../services/comment.service';

const FETCH_REPORT_COMMENTS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

interface UseReportCommentsParams {
  workflowId?: string;
  periodId?: string;
  fundId?: string | null;
  fundName?: string | null;
  tableName?: string | null;
  fieldName?: string | null;
  fieldOrder?: string | number | null;
  reportType?: string;
}

interface CommentThread {
  workflow_id?: string;
  fund_id?: string;
  fund_name?: string;
  field_name?: string;
  table_name?: string;
  order?: string;
  report_type?: string;
  messages?: unknown[];
  [key: string]: unknown;
}

type RefetchFn = (...args: any[]) => Promise<any>;

interface UseReportCommentsReturn {
  data: CommentThread[];
  cellThreadComments: unknown[];
  cellThreadData: CommentThread | Record<string, never>;
  refetchComments: RefetchFn;
}

const useReportComments = ({
  workflowId,
  periodId,
  fundId,
  fundName,
  tableName,
  fieldName,
  fieldOrder,
  reportType
}: UseReportCommentsParams = {}): UseReportCommentsReturn => {
  const { data: threads = [], refetch } = useAllComments({
    queryOptions: {
      refetchOnMount: false,
      refetchOnWindowFocus: true,
      staleTime: FETCH_REPORT_COMMENTS_STALE_TIME
    },
    params: {
      workflow_id: workflowId,
      period_id: periodId
    }
  });

  const filteredCellComments = useMemo(() => {
    return (threads as CommentThread[]).filter((thread: CommentThread) => {
      const matchesWorkflow = thread.workflow_id === workflowId;
      const matchesFund = fundId ? thread.fund_id === fundId : thread.fund_name === fundName;
      const matchesField = thread.field_name === fieldName;
      const matchesTableName = thread.table_name === tableName;
      const matchesFieldOrder = fieldOrder ? thread.order === `${fieldOrder}` : true;
      const matchesReportType = thread.report_type === reportType;
      return (
        matchesWorkflow &&
        matchesFund &&
        matchesField &&
        matchesTableName &&
        matchesFieldOrder &&
        matchesReportType
      );
    })[0];
  }, [threads, workflowId, fundId, fundName, fieldName, tableName, fieldOrder, reportType]);

  return {
    data: threads as CommentThread[],
    cellThreadComments: filteredCellComments?.messages || [],
    cellThreadData: filteredCellComments || {},
    refetchComments: refetch
  };
};

export default useReportComments;
